import * as React from 'react';
import { useMemo } from 'react';
import Media from 'react-media';

import DesktopSearchFilters from 'bundles/enterprise-collections/components/filters/DesktopSearchFilters';
import { DOMAIN_PAGE_SEARCH_FILTERS } from 'bundles/enterprise-learner-search/constants';
import { filterHiddenFilters } from 'bundles/enterprise-learner-search/utils/search';
import { useUserAgent } from 'bundles/page/contexts/UserAgentApiContext';
import SearchFiltersComponent from 'bundles/search-common/components/filters/SearchFilters';
import { useSearchFilters } from 'bundles/search-common/components/filters/useSearchFilters';
import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { FILTER_OVERFLOW_WIDTH } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';

import 'css!./__styles__/SearchFilters';

export const SearchFilters = () => {
  const { addFilters, removeFilters, isLoading } = useSearch();
  const userAgent = useUserAgent();

  // Use unified search filters hook with enterprise domain configuration
  // Note: disableCustomOrderOverrides allows pure count-based sorting
  const { filterKeyToOptionsMap, facetFilters, filterGroupConfigs } = useSearchFilters({
    facetOrder: DOMAIN_PAGE_SEARCH_FILTERS,
    sortConfig: {
      sortByCount: true, // Sort by count (descending)
      disableCustomOrderOverrides: true, // Disable difficulty/duration/type overrides for pure count sorting
    },
  });

  // Process active filters and remove hidden filters
  const activeFilters = useMemo(() => [...new Set(facetFilters?.flat() || [])], [facetFilters]);
  const activeFiltersToShow = useMemo(() => activeFilters.filter(filterHiddenFilters), [activeFilters]);

  // Create eventing data
  const eventingData = useMemo(() => {
    return { searchIndex: 'enterprise-search', searchIndexPosition: 0 };
  }, []);

  return (
    <div id="main" className="rc-SearchFilters">
      <Media query={{ maxWidth: FILTER_OVERFLOW_WIDTH }} defaultMatches={userAgent && userAgent.isMobileBrowser}>
        {(matches) => (
          <div className="SearchFilters-container">
            {matches ? (
              <SearchFiltersComponent
                filters={filterKeyToOptionsMap}
                activeFilters={activeFiltersToShow}
                eventingData={eventingData}
                facetOrder={DOMAIN_PAGE_SEARCH_FILTERS}
                facets={filterGroupConfigs}
                addFilters={(filterItem: string) => {
                  addFilters(SEARCH_MAIN_INDEX_ID, [filterItem]);
                }}
                removeFilters={(filterItem: string) => {
                  removeFilters(SEARCH_MAIN_INDEX_ID, [filterItem]);
                }}
                clearFilters={() => {
                  removeFilters(SEARCH_MAIN_INDEX_ID, activeFiltersToShow);
                  // not using clearFilters because we shouldn't remove hidden filters
                }}
                isMobile
              />
            ) : (
              <div className="DesktopSearchFiltersContainer">
                <DesktopSearchFilters
                  activeFilters={activeFiltersToShow}
                  filters={filterKeyToOptionsMap}
                  addFilters={(filterItem: string) => {
                    addFilters(SEARCH_MAIN_INDEX_ID, [filterItem]);
                  }}
                  removeFilters={(filtersToRemove: string[]) => {
                    removeFilters(SEARCH_MAIN_INDEX_ID, filtersToRemove);
                  }}
                  clearFilters={() => {
                    removeFilters(SEARCH_MAIN_INDEX_ID, activeFiltersToShow);
                    // not using clearFilters because we shouldn't remove hidden filters
                  }}
                  isLoading={isLoading}
                />
              </div>
            )}
          </div>
        )}
      </Media>
    </div>
  );
};

export default SearchFilters;
